import { settingsService } from '@/services/settingsService';

export class URLValidator {
    static isProbablySafeUrl(url: string): boolean {
        try {
            const parsedUrl = new URL(url);
            return ['http:', 'https:'].includes(parsedUrl.protocol) && Boolean(parsedUrl.hostname);
        } catch {
            return false;
        }
    }

    static async isSafeUrl(url: string): Promise<boolean> {
        try {
            const parsedUrl = new URL(url);

            // Get dynamic settings
            const suspiciousDomains = await settingsService.getSuspiciousDomains();
            const allowedDomains = await settingsService.getAllowedDomains();

            // Check if domain is in suspicious list (block URL shorteners)
            const isSuspiciousDomain = suspiciousDomains.some(domain =>
                parsedUrl.hostname === domain || parsedUrl.hostname.endsWith(`.${domain}`)
            );

            if (isSuspiciousDomain) {
                return false;
            }

            // Check if domain is in allowed list
            const isAllowedDomain = allowedDomains.some(domain =>
                parsedUrl.hostname === domain || parsedUrl.hostname.endsWith(`.${domain}`)
            );

            // Additional security checks
            if (!isAllowedDomain) return false;

            // Ensure it's http or https
            if (!['http:', 'https:'].includes(parsedUrl.protocol)) return false;

            // Basic URL structure validation
            if (!parsedUrl.hostname || parsedUrl.hostname.length === 0) return false;

            // Check for potentially malicious patterns
            if (this.containsMaliciousPatterns(url)) return false;

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Check for potentially malicious URL patterns
     */
    private static containsMaliciousPatterns(url: string): boolean {
        const maliciousPatterns = [
            // SQL injection attempts
            /(\b(union|select|insert|delete|update|drop|create|alter)\b)/i,
            // Script injection
            /<script/i,
            /javascript:/i,
            /data:text\/html/i,
            // Common exploit patterns
            /\.\./, // Directory traversal
            /%2e%2e/i, // URL encoded directory traversal
            // Excessive parameters that might indicate attacks
            /\?[^?]*\?/, // Multiple query strings
        ];

        return maliciousPatterns.some(pattern => pattern.test(url));
    }

    /**
     * Enhanced URL validation with content type checking
     * Note: This would typically be done server-side with actual HTTP requests
     */
    static async validateUrlContent(url: string): Promise<{
        isValid: boolean;
        contentType?: string;
        warnings?: string[];
    }> {
        // Client-side content validation is limited
        // This would be better implemented server-side
        const warnings: string[] = [];

        try {
            const parsedUrl = new URL(url);

            // Check for suspicious query parameters
            if (parsedUrl.searchParams.has('exec') || parsedUrl.searchParams.has('eval')) {
                warnings.push('URL contains potentially dangerous query parameters');
            }

            // Check for very long URLs that might be obfuscated
            if (url.length > 2000) {
                warnings.push('URL is unusually long and may be obfuscated');
            }

            // Check for excessive subdomains
            const subdomainCount = parsedUrl.hostname.split('.').length - 2; // Subtract TLD and main domain
            if (subdomainCount > 3) {
                warnings.push('URL has excessive subdomains which may indicate malicious intent');
            }

            return {
                isValid: warnings.length === 0,
                warnings: warnings.length > 0 ? warnings : undefined
            };

        } catch {
            return { isValid: false };
        }
    }

    static async validateUrls(urls: string[]): Promise<{ valid: string[], invalid: string[] }> {
        const valid: string[] = [];
        const invalid: string[] = [];

        for (const url of urls) {
            const trimmedUrl = url.trim();
            if (trimmedUrl && await this.isSafeUrl(trimmedUrl)) {
                valid.push(trimmedUrl);
            } else if (trimmedUrl) {
                invalid.push(trimmedUrl);
            }
        }

        return { valid, invalid };
    }

    static getDomainFromUrl(url: string): string | null {
        try {
            const parsedUrl = new URL(url);
            return parsedUrl.hostname;
        } catch {
            return null;
        }
    }
}